import type { LucideIcon } from "lucide-react";

import {
  ctaButtonBaseClassName,
  darkGradient,
  lightGradient,
} from "./ctaButtonStyles";

export interface Service {
  Icon: LucideIcon;
  title: string;
  description: string;
}

export interface ServicesCopy {
  header: {
    h2: string;
    h3: string;
    p: string;
  };
  services: Service[];
  cta: {
    h4: string;
    p: string;
    button: {
      text: string;
      href: string;
    };
  };
}

function getCtaColSpan(serviceCount: number, columns: number): number {
  const remainder = serviceCount % columns;
  return remainder === 0 ? columns : columns - remainder;
}

const MD_CTA_COL_SPAN: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
};

const LG_CTA_COL_SPAN: Record<number, string> = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
};

export function Services({ copy }: { copy: ServicesCopy }) {
  const ctaMdColSpan = getCtaColSpan(copy.services.length, 2);
  const ctaLgColSpan = getCtaColSpan(copy.services.length, 3);

  return (
    <section id="uslugi" className="py-24 bg-background-light">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2
            className={`text-sm font-semibold bg-clip-text text-transparent uppercase tracking-widest mb-3`}
            style={{ backgroundImage: darkGradient }}
          >
            {copy.header.h2}
          </h2>
          <h3 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            {copy.header.h3}
          </h3>
          <p className="text-lg text-slate-600">{copy.header.p}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {copy.services.map(service => (
            <div
              key={service.title}
              className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
            >
              <div className="w-12 mb-6">
                <div className="flex-shrink-0 mt-1 relative">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center opacity-40"
                    style={{ backgroundImage: lightGradient }}
                  />
                  <service.Icon className="absolute w-6 h-6 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-600" />
                </div>
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">
                {service.title}
              </h4>
              <p className="text-slate-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}

          <div
            className={`${ctaButtonBaseClassName} rounded-2xl p-8 text-gray-700 flex flex-col justify-center items-start col-span-1 ${MD_CTA_COL_SPAN[ctaMdColSpan]} ${LG_CTA_COL_SPAN[ctaLgColSpan]}`}
          >
            <h4 className="text-2xl font-semibold mb-4">{copy.cta.h4}</h4>
            <p className="mb-6 text-gray-700 font-normal">{copy.cta.p}</p>
            <a
              href={copy.cta.button.href}
              className="bg-white text-gray-700 font-medium py-3 px-6 rounded-full hover:bg-blue-50 transition-colors"
            >
              {copy.cta.button.text}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
